import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const PricingSection = () => {
  const [pricing, setPricing] = useState(null);
  useEffect(() => { axios.get(`${API}/pricing`).then((r) => setPricing(r.data)).catch(() => {}); }, []);
  if (!pricing) return null;
  return (
    <section className="py-16 bg-gray-50" id="pricing">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-2">Simple, honest pricing</h2>
        <p className="text-center text-gray-600 mb-2">
          {pricing.beta_slots_remaining > 0
            ? `First ${pricing.beta_slots_total} shelters get ${pricing.beta_days} days free — ${pricing.beta_slots_remaining} spots left.`
            : `Start with a ${pricing.trial_days}-day free trial. No card required.`}
        </p>
        <p className="text-center text-gray-500 mb-10">Cancel anytime · Grant-fundable (HUD CoC & state housing grants)</p>
        <div className="grid md:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {Object.entries(pricing.tiers).map(([k, t]) => (
            <div key={k} className={`rounded-xl border p-6 bg-white flex flex-col ${k === 'standard' ? 'border-indigo-500 shadow-lg ring-1 ring-indigo-200' : 'border-gray-200'}`}>
              {k === 'standard' && <div className="text-xs font-semibold text-indigo-600 mb-2">MOST POPULAR</div>}
              <div className="text-lg font-bold text-gray-900">{t.name}</div>
              <div className="text-sm text-gray-500 mb-4">Up to {t.max_beds} beds</div>
              <div className="text-3xl font-bold text-gray-900">${t.monthly}<span className="text-base font-normal text-gray-500">/mo</span></div>
              <div className="text-xs text-gray-500 mb-6">or ${t.annual}/yr (2 months free)</div>
              <Link to="/signup" className={`mt-auto text-center px-4 py-2 rounded-lg font-semibold ${k === 'standard' ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`}>
                Start free
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Homepage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">
            Transform Your Shelter Operations
          </h1>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Comprehensive management system built specifically for homeless shelters. 
            Streamline intake, staff scheduling, meal tracking, and reporting in one platform.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/signup" 
              className="bg-green-500 hover:bg-green-600 px-8 py-3 rounded-lg text-lg font-semibold inline-block"
              data-testid="hero-start-free"
            >
              Start Free — No Card
            </Link>
            <Link 
              to="/demo" 
              className="bg-white/10 hover:bg-white/20 border-2 border-white/60 px-8 py-3 rounded-lg text-lg font-semibold inline-block"
              data-testid="hero-try-demo"
            >
              Try the Demo
            </Link>
            <Link 
              to="/login" 
              className="bg-transparent border-2 border-white hover:bg-white hover:text-blue-800 px-8 py-3 rounded-lg text-lg font-semibold inline-block"
            >
              Login to Your Shelter
            </Link>
          </div>
          <p className="text-blue-100 text-sm mt-4">No sign-up needed to explore the demo · Full features with sample data</p>
        </div>
      </section>

      <PricingSection />

      {/* Beta Testing Announcement */}
      <section className="bg-green-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-green-800 mb-4">
            🎯 FREE Beta Testing Program Now Open
          </h2>
          <p className="text-lg text-green-700 mb-6">
            Get 3 months of FREE access to our complete shelter management system
          </p>
          
          <div className="bg-white p-8 rounded-lg max-w-4xl mx-auto shadow-lg">
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">100% FREE</div>
                <div className="text-gray-600">Complete Access</div>
                <div className="text-sm text-gray-500">3 months duration</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">10 SPOTS</div>
                <div className="text-gray-600">Beta Partners</div>
                <div className="text-sm text-gray-500">Limited availability</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">24/7</div>
                <div className="text-gray-600">Support</div>
                <div className="text-sm text-gray-500">Dedicated team</div>
              </div>
            </div>
            
            <Link 
              to="/beta" 
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold inline-block text-lg"
            >
              Apply for Beta Testing →
            </Link>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Everything Your Shelter Needs
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <div className="text-5xl mb-4">👥</div>
              <h3 className="text-xl font-semibold mb-3">Staff Management</h3>
              <p className="text-gray-600">Time clock, scheduling, payroll tracking, and shift communication</p>
            </div>
            
            <div className="text-center p-6">
              <div className="text-5xl mb-4">🏠</div>
              <h3 className="text-xl font-semibold mb-3">Resident Services</h3>
              <p className="text-gray-600">HMIS intake, case notes, room assignments, and status tracking</p>
            </div>
            
            <div className="text-center p-6">
              <div className="text-5xl mb-4">🍽️</div>
              <h3 className="text-xl font-semibold mb-3">Meal Tracking</h3>
              <p className="text-gray-600">Daily planning, service logging, and monthly reporting with analytics</p>
            </div>
            
            <div className="text-center p-6">
              <div className="text-5xl mb-4">📊</div>
              <h3 className="text-xl font-semibold mb-3">Reporting</h3>
              <p className="text-gray-600">Real-time dashboard, HMIS compliance, and detailed analytics</p>
            </div>
          </div>
        </div>
      </section>

      {/* Simple CTA */}
      <section className="bg-blue-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8">
            Join our beta testing program for FREE access to all features
          </p>
          <Link 
            to="/beta" 
            className="bg-green-500 hover:bg-green-600 px-8 py-4 rounded-lg text-lg font-semibold inline-block"
          >
            Apply for Beta Testing
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Homepage;